/**
 * Track the user's last-visited projects locally for "Continuar de onde parou".
 *
 * Stores under `last_visited_projects` in localStorage as a most-recent-first
 * list of up to MAX_HISTORY entries. Each entry:
 *   { project_id, name, project_type, overall_percentage, visited_at }
 */
import { useEffect } from 'react';

const STORAGE_KEY = 'last_visited_projects';
const LEGACY_KEY = 'last_visited_project';  // pre-v2 single-entry storage
const MAX_HISTORY = 5;

function readRaw() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    }
    // Migrate legacy single-entry storage into the new array
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy);
      localStorage.removeItem(LEGACY_KEY);
      const arr = parsed?.project_id ? [parsed] : [];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
      return arr;
    }
    return [];
  } catch {
    return [];
  }
}

export function rememberLastProject(project) {
  if (!project?.project_id) return;
  try {
    const list = readRaw().filter((p) => p.project_id !== project.project_id);
    list.unshift({
      project_id: project.project_id,
      name: project.name || '',
      project_type: project.project_type || '',
      overall_percentage: project.overall_percentage || 0,
      visited_at: new Date().toISOString(),
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_HISTORY)));
  } catch {
    // localStorage unavailable (private mode etc.) — silently ignore
  }
}

export function readLastProjects() {
  return readRaw();
}

export function dismissProject(projectId) {
  try {
    const list = readRaw().filter((p) => p.project_id !== projectId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    return list;
  } catch {
    return [];
  }
}

export function clearLastProjects() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

// --- back-compat exports for any callers still importing the v1 names ---
export const readLastProject = () => readRaw()[0] || null;
export const clearLastProject = () => clearLastProjects();

/**
 * Hook that records a visit whenever `project` becomes truthy.
 */
export function useRememberLastProject(project) {
  useEffect(() => {
    if (project?.project_id) rememberLastProject(project);
  }, [project?.project_id, project?.overall_percentage]);
}
